// TYPES
import {
  Component,
  OnContinue,
  Option,
  PersonType,
  PitbullProvinces,
  SignatoryData,
  Step,
  StepTwoPJ,
  StepThreePJ,
  StepFourPJ,
} from "../../types";

// UTILS
import { clearAddressOf, input } from "../../inputs";
import {
  mediumSelectedAddressRequired,
  isEmailExcludedFrom,
  largeStringRequired,
  multipleSelectRequired,
  radioOrSingleSelectRequired,
  selectedAddressRequired,
} from "../../validations";
import {
  businessDocuments,
  capitalizeString,
  formatDNI,
  fuseProvince,
  getDraftStepsToSend,
  getForeignTaxDetails,
  buildFullAddress,
} from "../../app/utils";
import {
  asDni,
  asHighlighted,
  convertStringToBoolean,
  isCompanyWithoutShareholders,
  isMobileViewport,
} from "../../utils";
import { getCompanyData } from "../../utils/companies";
import { sendStep } from "../../connectors/connectors";
import {
  addSendedStep,
  onUpdateCompletedStep,
  onUpdateDisabledSubStep,
  onUpdateFormData,
  onUpdateReachedStep,
  onUpdateStep,
} from "../../reducers/onboardingDataReducer";
import { onUpdateActiveIndex } from "../../reducers/collectionReducer";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";

// COMPONENTS
import EntitySelector from "../../components/EntitySelector/EntitySelector";

import stylesSupplementaryComponentInfoRenderer from "../../components/SupplementaryComponentInfoRenderer/SupplementaryComponentInfoRenderer.module.scss";

// CONDITIONALS
const ifBusinessForeignTaxResidence = {
  name: "businessInformation.foreignTaxDetailBool",
  value: "SI",
};

const entityStep = (
  countriesOptions: Option[],
  activitiesOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
): Omit<Step, "step"> => {
  const foreignTaxCountryOptions = countriesOptions.filter(
    (country) => country.value !== "Argentina",
  );
  const provincesOption = provinces.map((prov) => ({
    value: prov.name,
    label: prov.name,
  }));

  const isMobile = isMobileViewport();
  const signatoriesSubStep = isMobile ? 4 : 3;

  const entityQuestionsFields = [
    input("hasJointSignature"),
    input("isPublicEntity"),
    input("isInEconomicGroup"),
    input("isComplexStructure"),
  ];

  const bankAndForeignTaxFields = [
    input("bankAccountBool"),
    input("bankAccounts"),
    input("businessForeignTaxDetailBool", { columns: 2 }),
    input("address.country", {
      name: "businessInformation.foreignTaxDetail.country",
      label: "País de tributación",
      validation: radioOrSingleSelectRequired(foreignTaxCountryOptions),
      options: foreignTaxCountryOptions,
      conditional: ifBusinessForeignTaxResidence,
      onChange: clearAddressOf("businessInformation.foreignTaxDetail"),
      columns: 2,
    }),
    input("address.fullAddress", {
      name: "businessInformation.foreignTaxDetail.fullAddress",
      autoSelect: "businessInformation.foreignTaxDetail.fullAddress",
      country: "businessInformation.foreignTaxDetail.country",
      validation: mediumSelectedAddressRequired,
      conditional: ifBusinessForeignTaxResidence,
      onChange: (values, setFieldValue) => {
        const {
          formattedAddress: {
            postalCode: zipCode,
            state,
            city,
            municipality,
            street,
            streetNumber: number,
            country,
          },
        } = values;

        const provinces = getProvincesFilteredByCountry(country);

        const fusedProvince = fuseProvince(provinces, state, city);

        //// If no records are found in FUSE, we set the first province by default to avoid breaking
        const finalProvince = fusedProvince || provinces[0].name;

        setFieldValue("businessInformation.foreignTaxDetail.zipCode", zipCode);
        setFieldValue(
          "businessInformation.foreignTaxDetail.state",
          finalProvince,
        );
        setFieldValue("businessInformation.foreignTaxDetail.city", city);
        setFieldValue(
          "businessInformation.foreignTaxDetail.municipality",
          municipality,
        );
        setFieldValue("businessInformation.foreignTaxDetail.street", street);
        setFieldValue("businessInformation.foreignTaxDetail.number", number);

        const newFullAddress = buildFullAddress(
          street,
          number,
          city,
          municipality,
        );

        setFieldValue(
          "businessInformation.foreignTaxDetail.fullAddress",
          newFullAddress,
        );
      },
      formatInPreConfirmation: (value) => (value ? value?.split(",")?.[0] : ""),
    }),
    input("address.floor", {
      name: "businessInformation.foreignTaxDetail.floor",
      conditional: ifBusinessForeignTaxResidence,
    }),
    input("address.apartment", {
      name: "businessInformation.foreignTaxDetail.apartment",
      conditional: ifBusinessForeignTaxResidence,
    }),
    input("address.zipCode", {
      name: "businessInformation.foreignTaxDetail.zipCode",
      conditional: ifBusinessForeignTaxResidence,
    }),
    input("address.municipality", {
      name: "businessInformation.foreignTaxDetail.municipality",
      conditional: ifBusinessForeignTaxResidence,
    }),
    input("address.state", {
      name: "businessInformation.foreignTaxDetail.state",
      validation: radioOrSingleSelectRequired(provincesOption),
      conditional: ifBusinessForeignTaxResidence,
      dynamicOptions: (formik) => {
        const country =
          formik.values.businessInformation.foreignTaxDetail?.country;
        const provinces = getProvincesFilteredByCountry(country);
        const provincesOption = provinces.map((prov) => ({
          value: prov.name,
          label: prov.name,
        }));
        return provincesOption;
      },
    }),
    input("businessForeignTaxId"),
  ];

  const sendEntityData: OnContinue = async (values, dispatch, state) => {
    if (!state || !dispatch)
      throw new Error("Error: state or dispatch not found");
    const {
      email,
      businessInformation: {
        cuit,
        legalName,
        corporateType,
        activities,
        activitiesDetails,
        registrationNumber,
        registrationAuthority,
        registrationAuthorityDetails,
        registrationCountry,
        registrationDate,
        registrationProvince,
        isPublicEntity,
        hasJointSignature,
        isInEconomicGroup,
        isComplexStructure,
        bankAccountBool,
        bankAccounts,
        foreignTaxDetailBool,
        foreignTaxDetail,
      },
      entityAddress: {
        apartment,
        city,
        floor,
        municipality,
        number,
        state: stateAddress,
        street,
        zipCode,
      },
    } = values;

    const getBankAccounts = () => {
      return bankAccountBool === "SI" ? [{ cxu: bankAccounts }] : [];
    };

    const stepTwo: StepTwoPJ = {
      "@type": "2_JURIDICAL",
      businessInformation: {
        cuit,
        legalName,
        corporateType,
        activities,
        activitiesDetails: activitiesDetails !== "" ? activitiesDetails : null,
        registrationNumber,
        registrationAuthority,
        registrationAuthorityDetails:
          registrationAuthorityDetails !== ""
            ? registrationAuthorityDetails
            : null,
        registrationCountry,
        registrationDate,
        registrationProvince,
        isPublicEntity: convertStringToBoolean(isPublicEntity),
        hasJointSignature: convertStringToBoolean(hasJointSignature),
        isInEconomicGroup: convertStringToBoolean(isInEconomicGroup),
        isComplexStructure: convertStringToBoolean(isComplexStructure),
        addresses: [
          {
            street,
            number,
            zipCode,
            city,
            state: stateAddress,
            country: "Argentina",
            floor,
            apartment,
            municipality,
          },
        ],
        bankAccounts: [...getBankAccounts()],
        ...getForeignTaxDetails(foreignTaxDetailBool, foreignTaxDetail),
      },
    };

    const { sendedSteps } = state.onboardingData;
    const stepsWithStepTwo = getDraftStepsToSend(sendedSteps, stepTwo);

    const body = {
      companyType: getCompanyData().companyName,
      personType: state.activeTemplate.onboardingType as PersonType,
      email,
      steps: stepsWithStepTwo,
    };
    await sendStep({ stepId: 2, body });
    dispatch(addSendedStep(stepTwo));

    if (hasJointSignature === "SI") return;

    const stepThree: StepThreePJ = {
      "@type": "3_JURIDICAL",
      additionalSignatories: [],
    };
    const stepsWithStepThree = getDraftStepsToSend(stepsWithStepTwo, stepThree);
    await sendStep({
      stepId: 3,
      body: {
        companyType: getCompanyData().companyName,
        personType: state.activeTemplate.onboardingType as PersonType,
        email,
        steps: stepsWithStepThree,
      },
    });
    dispatch(addSendedStep(stepThree));
    dispatch(onUpdateCompletedStep(5));
    dispatch(onUpdateDisabledSubStep(signatoriesSubStep));

    if (isCompanyWithoutShareholders(corporateType)) {
      const stepFour: StepFourPJ = {
        "@type": "4_JURIDICAL",
        shareholders: [],
      };

      const stepsWithStepFour = getDraftStepsToSend(
        stepsWithStepThree,
        stepFour,
      );

      await sendStep({
        stepId: 4,
        body: {
          companyType: getCompanyData().companyName,
          personType: "JURIDICAL",
          email: values.email || "",
          steps: stepsWithStepFour,
        },
      });

      dispatch(addSendedStep(stepFour));
    }
  };

  const entityDataSubSteps = isMobile
    ? [
        { step: 2, fields: entityQuestionsFields },
        {
          step: 3,
          fields: bankAndForeignTaxFields,
          onContinue: sendEntityData,
        },
      ]
    : [
        {
          step: 2,
          fields: [...entityQuestionsFields, ...bankAndForeignTaxFields],
          onContinue: sendEntityData,
        },
      ];

  return {
    name: "Datos de la entidad",
    titleOfStep: "Entidad",
    subSteps: [
      {
        step: 1,
        fields: [
          input("legalName", {
            name: "businessInformation.legalName",
            validation: largeStringRequired,
            disabled: (state) => {
              const { formValues } = state.onboardingData;
              return !!formValues?.businessInformation?.legalName;
            },
          }),
          input("address.fullAddress", {
            name: "entityAddress.fullAddress",
            validation: selectedAddressRequired,
            autoSelect: "entityAddress.fullAddress",
            country: "Argentina",
            onChange: (values, setFieldValue) => {
              const {
                formattedAddress: {
                  postalCode: zipCode,
                  state,
                  city,
                  municipality,
                  street,
                  streetNumber: number,
                  country,
                },
              } = values;

              const provinces = getProvincesFilteredByCountry(country);

              const fusedProvince = fuseProvince(provinces, state, city);

              //// If no records are found in FUSE, we set the first province by default to avoid breaking
              const finalProvince = fusedProvince || provinces[0].name;

              setFieldValue("entityAddress.zipCode", zipCode);
              setFieldValue("entityAddress.state", finalProvince);
              setFieldValue("entityAddress.city", city);
              setFieldValue("entityAddress.municipality", municipality);
              setFieldValue("entityAddress.street", street);
              setFieldValue("entityAddress.number", number);

              const newFullAddress = buildFullAddress(
                street,
                number,
                city,
                municipality,
              );

              setFieldValue("entityAddress.fullAddress", newFullAddress);
            },
          }),
          input("address.floor", { name: "entityAddress.floor" }),
          input("address.apartment", { name: "entityAddress.apartment" }),
          input("address.zipCode", { name: "entityAddress.zipCode" }),
          input("address.municipality", {
            name: "entityAddress.municipality",
          }),
          input("address.state", {
            name: "entityAddress.state",
            validation: radioOrSingleSelectRequired(provincesOption),
            dynamicOptions: (formik) => {
              const country = formik.values.entityAddress?.country;

              //Filter provinces by country
              //In this case is always Argentina
              const provinces = getProvincesFilteredByCountry(country);

              const provincesOption = provinces.map((prov) => ({
                value: prov.name,
                label: prov.name,
              }));

              return provincesOption;
            },
          }),
          input("corporateType", {
            onChange: (_, setFieldValue, __, ___, ____, _____, dispatch) => {
              if (!dispatch) throw new Error("Error: dispatch not found");

              const businessDocumentationFields = Object.keys(
                businessDocuments,
              ).map((document) => `businessDocumentation.${document}`);

              businessDocumentationFields.forEach((field) => {
                setFieldValue(field, "");
              });

              dispatch(onUpdateCompletedStep(4));
              dispatch(onUpdateReachedStep(5));
            },
          }),
          input("activities", {
            name: "businessInformation.activities",
            validation: multipleSelectRequired(activitiesOptions),
            options: activitiesOptions,
            columns: 2,
          }),
          input("activitiesDetails", {
            name: "businessInformation.activitiesDetails",
            columns: 2,
          }),
          input("registrationCountry", {
            validation: radioOrSingleSelectRequired(countriesOptions),
            options: countriesOptions,
            onChange: (_, setFieldValue) => {
              setFieldValue("businessInformation.registrationProvince", "");
            },
          }),
          input("registrationDate"),
          input("registrationProvince", {
            validation: radioOrSingleSelectRequired(provincesOption),
            dynamicOptions: (formik) => {
              const country =
                formik.values.businessInformation?.registrationCountry ||
                "Argentina";

              //Filter provinces by country
              const provinces = getProvincesFilteredByCountry(country);

              const provincesOption = provinces.map((prov) => ({
                value: prov.name,
                label: prov.name,
              }));

              return provincesOption;
            },
          }),
          input("registrationNumber"),
          input("registrationAuthority"),
          input("registrationAuthorityDetails"),
        ],
      },
      ...entityDataSubSteps,
      {
        step: signatoriesSubStep,
        name: "Datos de la entidad",
        titleOfStep: isMobile
          ? "Quienes estarán a cargo en la plataforma"
          : undefined,
        subtitle: isMobile
          ? "Agregá hasta 3 personas. Cada persona recibirá un mail para finalizar el proceso de apertura de la cuenta."
          : undefined,
        component: ((dispatch, state, formik) => {
          if (!state || !dispatch) throw new Error("Error: state not found");

          const { formValues, sendedSteps } = state.onboardingData;
          const signatories: SignatoryData[] = formValues?.signatories || [];

          const emptySignatory: SignatoryData = {
            name: "",
            lastName: "",
            email: "",
            idNumber: "",
            confirmEmail: "",
          };

          const isCompletedSignatory = (signatory?: SignatoryData) =>
            !!signatory && Object.values(signatory).every((value) => value);

          return (
            <EntitySelector
              formik={formik}
              entityType="signatory"
              title={
                isMobile ? undefined : "Quienes estarán a cargo en la plataforma"
              }
              unsaveChangesAlert={{ title: "¿Descartar firmante?" }}
              entities={signatories.map(({ name, lastName, email }, index) => ({
                label: `Firmante ${index + 1}`,
                value:
                  name && lastName
                    ? `${name} ${lastName} ${
                        email && email === formValues?.email ? "(Tú)" : ""
                      }`
                    : "",
                placeholder: "Nombre Apellido",
              }))}
              handleEntities={() => {
                if (signatories.length === 0) {
                  const firstSignatory: SignatoryData = {
                    name: formValues?.name,
                    lastName: capitalizeString(formValues?.lastName),
                    email: formValues?.email,
                    idNumber: formValues?.identityDocument,
                    confirmEmail: formValues?.email,
                  };

                  if (isMobile) {
                    dispatch(
                      onUpdateFormData({ signatories: [firstSignatory] }),
                    );
                    return;
                  }

                  dispatch(onUpdateActiveIndex(2));
                  dispatch(
                    onUpdateFormData({
                      signatories: [firstSignatory, emptySignatory],
                    }),
                  );
                }

                // Joint signature always needs a second signatory to complete
                if (!isMobile && signatories.length === 1) {
                  dispatch(
                    onUpdateFormData({
                      signatories: [...signatories, emptySignatory],
                    }),
                  );
                }
              }}
              isCompletedEntity={(i: number) =>
                isCompletedSignatory(signatories[i])
              }
              addEntityButton={{
                text: `Agregar firmante ${signatories.length + 1}`,
                mobileText: "Cargar nuevo firmante",
                onClick: () =>
                  dispatch(
                    onUpdateFormData({
                      signatories: [...signatories, emptySignatory],
                    }),
                  ),
                hide: signatories.length >= 3,
                disabled: !isCompletedSignatory(
                  signatories[isMobile ? signatories.length - 1 : 1],
                ),
              }}
              infoAlert={{
                title: isMobile ? undefined : "Agregá hasta 3 personas",
                description: isMobile
                  ? "Cada mail que ingreses se vinculará permanentemente al firmante correspondiente. Aconsejamos que sean correos personales y con acceso directo, ya que no podrán desvincularse."
                  : "Cada persona recibirá un mail para finalizar el proceso de apertura de la cuenta. Recordá: Cada mail que ingreses se vinculará permanentemente al firmante correspondiente. Aconsejamos que sean correos personales y con acceso directo, ya que no podrán desvincularse.",
              }}
              disabledFirst
              onContinueButton={{
                onClick: async () => {
                  const additionalSignatories = signatories
                    .filter(isCompletedSignatory)
                    .filter((signer) => signer.email !== formValues?.email)
                    .map(({ name, lastName, email, idNumber }) => ({
                      name,
                      lastName,
                      email,
                      idNumber: formatDNI(idNumber || ""),
                    }));

                  const stepThree: StepThreePJ = {
                    "@type": "3_JURIDICAL",
                    additionalSignatories,
                  };

                  const stepsWithStepThree = getDraftStepsToSend(
                    sendedSteps,
                    stepThree,
                  );
                  await sendStep({
                    stepId: 3,
                    body: {
                      companyType: getCompanyData().companyName,
                      personType: "JURIDICAL",
                      email: formValues?.email || "",
                      steps: stepsWithStepThree,
                    },
                  });

                  dispatch(addSendedStep(stepThree));
                  dispatch(onUpdateCompletedStep(5));

                  const corporateType =
                    formValues?.businessInformation?.corporateType;

                  if (isCompanyWithoutShareholders(corporateType)) {
                    const stepFour: StepFourPJ = {
                      "@type": "4_JURIDICAL",
                      shareholders: [],
                    };

                    await sendStep({
                      stepId: 4,
                      body: {
                        companyType: getCompanyData().companyName,
                        personType: "JURIDICAL",
                        email: formValues?.email || "",
                        steps: getDraftStepsToSend(
                          stepsWithStepThree,
                          stepFour,
                        ),
                      },
                    });

                    dispatch(addSendedStep(stepFour));
                    dispatch(onUpdateStep(7));
                    dispatch(onUpdateCompletedStep(6));
                  } else {
                    dispatch(onUpdateStep(6));
                  }
                },
                disabled:
                  signatories.length < 2 ||
                  !signatories.every(isCompletedSignatory),
              }}
            />
          );
        }) as Component,
        insideForm: true,
        skipWhen: (values) =>
          values?.businessInformation?.hasJointSignature === "NO",
        secondaryForm: {
          title: (activeIndex) => `Firmante ${activeIndex}`,
          mobileTitle: "Cargar firmante",
          collectionName: "signatories",
          deleteEntity: {
            alert: {
              title: ({ name, lastName }) => (
                <>
                  ¿Eliminar a{" "}
                  {asHighlighted([name, lastName].filter(Boolean).join(" "))}{" "}
                  como firmante?
                </>
              ),
              message:
                "Esta acción no se puede deshacer. Para sumarlo de nuevo al listado, vas a tener que completar sus datos otra vez.",
            },
            // The account holder is always the first signatory
            hidden: (state) => state.collectionData.activeIndex === 1,
            onClick: (dispatch, state, setPrimaryFormValues) => {
              const { activeIndex } = state.collectionData;
              if (!activeIndex) return;

              const signatories: SignatoryData[] =
                state.onboardingData.formValues?.signatories || [];
              const index = activeIndex - 1;
              const newSignatories = signatories.filter((_, i) => i !== index);

              dispatch(onUpdateFormData({ signatories: newSignatories }));
              if (index !== 1) dispatch(onUpdateActiveIndex(index));

              setPrimaryFormValues?.((prevValues) => ({
                ...prevValues,
                signatories: newSignatories,
              }));
            },
          },
          fields: [
            input("name"),
            input("lastName"),
            input("identityDocument", {
              name: "idNumber",
              formatInPreConfirmation: (value) =>
                value ? asDni(value).replace(/^0/, "") : "",
            }),
            input("email", {
              conditionalValidation: (state) => {
                if (!state) throw new Error("Error: state not found");
                const activeIndex = state.collectionData.activeIndex;

                const { formValues } = state.onboardingData;
                const signatories: SignatoryData[] =
                  formValues?.signatories || [];
                const signatoriesEmail = signatories.map(({ email }) => email);
                const emailsExcludingActiveIndex = signatoriesEmail.filter(
                  (_, index) =>
                    activeIndex !== null && index !== activeIndex - 1,
                );

                return isEmailExcludedFrom(emailsExcludingActiveIndex);
              },
            }),
            input("confirmEmail", {
              onBlur: async (values, _, setFieldError, __, ___, state) => {
                if (!state) throw new Error("Error: state not found");

                const { formValues } = state.onboardingData;
                const signatories: SignatoryData[] =
                  formValues?.signatories || [];
                const signatoriesEmail = signatories.map(({ email }) => email);
                const value = values.email;

                if (
                  value &&
                  signatoriesEmail.includes(value) &&
                  setFieldError
                ) {
                  setFieldError(
                    "confirmEmail",
                    "No se puede utilizar el mismo mail para más de un firmante.",
                  );
                }
              },
              onChange: async (_values, setFieldValue, _, __, element) => {
                // To set error if the email is already in the list of signatories
                const value = element && element.target.value;
                await setFieldValue("confirmEmail", value);
              },
              disablePaste: true,
            }),
          ],
          onSave: async (
            values,
            dispatch,
            state,
            setPrimaryFormValues,
            formik,
          ) => {
            if (!state || !dispatch || !formik)
              throw new Error("Error: state or dispatch not found");

            const {
              onboardingData: { formValues },
              collectionData: { activeIndex },
            } = state;

            if (!activeIndex) throw new Error("Error: activeIndex not found");

            const signatories = formValues?.signatories || [];

            const newSignatories = [...signatories];
            newSignatories[activeIndex - 1] = values;

            setPrimaryFormValues &&
              setPrimaryFormValues((prevValues) => ({
                ...prevValues,
                signatories: newSignatories,
              }));
            dispatch(onUpdateFormData({ signatories: newSignatories }));
            formik.resetForm();
            showToastMessage("El firmante fue guardado exitosamente.", {
              containerId: "layoutPage",
              type: "SUCCESS",
            });
          },
        },
      },
    ],
    supplementaryComponentInfo: {
      show: true,
      position: "top",
      component: () => {
        return (
          <p
            className={stylesSupplementaryComponentInfoRenderer.emailValidation}
          >
            Validá tu dirección de mail para retomar el proceso de registro
          </p>
        );
      },
    },
  };
};

export default entityStep;
